import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import HomeSky from '@/components/home/HomeSky';
import WanderPanel from '@/components/home/WanderPanel';
import RhythmCard from '@/components/home/RhythmCard';
import PrideList from '@/components/home/PrideList';
import LivingBubble from '@/components/LivingBubble';
import AdventureLog from '@/components/home/AdventureLog';
import { IntroInbox, IntroSettings } from '@/components/home/Intros';
import NotificationsPanel from '@/components/NotificationsPanel';

const hour = () => new Date().getHours();

export default function BubbleHome() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data: d } = await api.get(`/home?hour=${hour()}`);
    setData(d);
  }, []);
  useEffect(() => { load().catch(() => setData({ bubble: null })); }, [load]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); await load(); if (msg) toast(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || 'something went wrong'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-[80vh] grid place-items-center text-white/70" data-testid="home-loading">listening for your bubble…</div>;

  if (!data.bubble) {
    return (
      <div className="min-h-[80vh] grid place-items-center text-center px-6" data-testid="home-empty">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-white">The sky is empty.</h1>
          <p className="mt-4 text-white/75">Your bubble hasn't been born yet.</p>
          <Link to="/express" data-testid="home-birth-btn" className="mt-8 inline-flex btn-volt px-7 py-3 rounded-full">Give birth to a bubble</Link>
        </div>
      </div>
    );
  }

  const { bubble, wander, rhythm, pride, sky, wander_minutes, places, first_adventure, intros, intro_settings } = data;
  const away = wander?.status === 'away' || wander?.status === 'resolving';
  const returned = wander?.status === 'returned';
  const h = hour();
  const mood = returned ? 'excited' : (h >= 22 || h < 6) ? 'sleepy' : rhythm?.slot === 'morning' ? 'curious' : 'happy';
  const cardActions = wander ? {
    onReply: (text) => act(() => api.post(`/home/wander/${wander.id}/reply`, { text })),
    onReact: (reaction) => act(() => api.post(`/home/wander/${wander.id}/react`, { reaction })),
    onIntroduce: () => act(() => api.post(`/home/wander/${wander.id}/introduce`), 'Asked. Nothing is shared unless they say yes too.'),
    onContinue: () => act(() => api.post(`/home/wander/${wander.id}/welcome`), `${bubble.name} is ready for the next one.`),
  } : {};

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden" data-testid="bubble-home">
      <HomeSky others={sky} away={away} />
      {/* Notifications bell — top right */}
      <div className="absolute top-4 right-4 z-30">
        <NotificationsPanel />
      </div>
      <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-10 pb-32 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
        <section className="flex flex-col items-center lg:items-start lg:sticky lg:top-24">
          <div className={`relative h-64 w-64 sm:h-80 sm:w-80 ${away ? 'home-gone' : returned ? 'home-return' : 'home-float'}`} data-testid="home-bubble-orb">
            <LivingBubble bubble={bubble} mood={mood} className="h-full w-full" testid="home-living-bubble" />
          </div>
          {away && <p className="mt-2 text-sm text-white/65" data-testid="home-empty-nest">an empty little nest where {bubble.name} usually floats</p>}
          <h1 className="mt-6 font-display text-4xl sm:text-5xl text-white" data-testid="home-bubble-name">{bubble.name}</h1>
          <p className="mt-2 text-white/75 text-sm" data-testid="home-bubble-meta">{/^[aeiou]/i.test(bubble.vibe || '') ? 'an' : 'a'} {bubble.vibe} soul · level {bubble.level || 1} · feeling {bubble.mood || 'calm'}</p>
          <Link to={`/bubble/${bubble.id}`} data-testid="home-talk-link" className="mt-4 text-sm text-bx-cyan hover:underline">Talk to {bubble.name} →</Link>
        </section>
        <section className="space-y-5">
          <IntroInbox intros={intros} busy={busy}
                      onRespond={(id, accept) => act(() => api.post(`/intros/${id}/respond`, { accept }), accept ? 'Introduced!' : 'Declined quietly.')} />
          <WanderPanel bubble={bubble} wander={wander} minutes={wander_minutes} first={first_adventure} places={places} busy={busy}
                       onSend={(place_id) => act(() => api.post('/home/wander', place_id ? { place_id } : {}), `${bubble.name} floated away…`)}
                       onDue={load} cardActions={cardActions} />
          <RhythmCard rhythm={rhythm} onDone={() => act(() => api.post(`/home/rhythm/done?hour=${hour()}`), 'It glows a little brighter.')} />
          <PrideList name={bubble.name} pride={pride} />
          <AdventureLog name={bubble.name} items={data.log} />
          <IntroSettings key={String(intro_settings?.allow_intros)} bubbleName={bubble.name} settings={intro_settings} busy={busy}
                         onSave={(body) => act(() => api.put('/home/intro-settings', body), 'Saved.')} />
        </section>
      </div>
    </div>
  );
}
